import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Star, Share2, Trash2, User, Video } from 'lucide-react';
import { useShareSettings } from '../../utils/shareSettings';
import ProfileCropModal from './ProfileCropModal';

/**
 * Pinterest-Style Fullscreen Image Viewer
 * TikTok-style instant swipe with snap
 */
const ImageFullscreenViewer = ({
  isOpen,
  onClose,
  images,
  initialIndex = 0,
  onToggleFavorite,
  onDelete,
  onSetProfileImage,
  onVideo,
  favorites = new Set(),
  shareTitle: shareTitleProp,
}) => {
  const shareSettings = useShareSettings();
  const shareTitle = shareTitleProp || shareSettings.photo_share_text;
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileToast, setProfileToast] = useState(false);
  const [showProfileCrop, setShowProfileCrop] = useState(false);
  const scrollYRef = useRef(0);
  const containerRef = useRef(null);
  
  // Zoom state
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const initialPinchRef = useRef(null);
  const lastTouchRef = useRef({ x: 0, y: 0 });
  
  // Swipe state
  const trackRef = useRef(null);
  const swipeStartRef = useRef(null);
  const swipeOffsetRef = useRef(0);

  const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 375;

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setScale(1);
      setPosition({ x: 0, y: 0 });
      swipeOffsetRef.current = 0;
      updateTrackPosition(initialIndex, false);
    }
  }, [isOpen, initialIndex]);

  // Update track when index changes
  useEffect(() => {
    updateTrackPosition(currentIndex, true);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [currentIndex]);

  // Keep index valid
  useEffect(() => {
    if (images.length > 0 && currentIndex >= images.length) {
      setCurrentIndex(images.length - 1);
    }
  }, [images.length, currentIndex]);

  // Update track position
  const updateTrackPosition = useCallback((index, animate) => {
    if (trackRef.current) {
      const offset = -index * screenWidth;
      trackRef.current.style.transition = animate ? 'transform 0.2s ease-out' : 'none';
      trackRef.current.style.transform = `translate3d(${offset}px, 0, 0)`;
    }
  }, [screenWidth]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      scrollYRef.current = window.scrollY;
      document.body.style.cssText = `
        position: fixed;
        top: -${scrollYRef.current}px;
        left: 0;
        right: 0;
        overflow: hidden;
        width: 100%;
        overscroll-behavior: none;
      `;
      document.documentElement.style.overscrollBehavior = 'none';
      
      return () => {
        document.body.style.cssText = '';
        document.documentElement.style.overscrollBehavior = '';
        window.scrollTo(0, scrollYRef.current);
      };
    }
  }, [isOpen]);

  const currentImage = images[currentIndex];
  const isFavorite = currentImage && favorites.has(currentImage.id);
  
  // Format aus Bildmaßen berechnen
  const [calculatedFormat, setCalculatedFormat] = useState(null);
  
  const calculateAspectRatio = (width, height) => {
    const ratio = width / height;
    if (ratio > 1.7) return '16:9';
    if (ratio > 1.2) return '4:3';
    if (ratio > 0.9) return '1:1';
    if (ratio > 0.7) return '3:4';
    return '9:16';
  };
  
  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setCalculatedFormat(calculateAspectRatio(naturalWidth, naturalHeight));
  };
  
  // Format: gespeichert > berechnet
  const displayFormat = currentImage?.aspect_ratio || calculatedFormat;

  // Navigation
  const goToIndex = useCallback((index) => {
    const newIndex = Math.max(0, Math.min(images.length - 1, index));
    setCurrentIndex(newIndex);
  }, [images.length]);

  // Pinch helpers
  const getPinchDistance = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  const getPinchCenter = (t) => ({ x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 });

  // Touch handlers
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Pinch start
      initialPinchRef.current = {
        distance: getPinchDistance(e.touches),
        scale: scale,
        center: getPinchCenter(e.touches),
        position: { ...position }
      };
    } else if (e.touches.length === 1) {
      const touch = e.touches[0];
      lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
      
      if (scale <= 1) {
        // Start swipe
        swipeStartRef.current = touch.clientX;
        swipeOffsetRef.current = 0;
      }
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    
    if (e.touches.length === 2 && initialPinchRef.current) {
      // Pinch zoom - continuous update
      const newDist = getPinchDistance(e.touches);
      const scaleFactor = newDist / initialPinchRef.current.distance;
      let newScale = initialPinchRef.current.scale * scaleFactor;
      
      // Clamp scale between 1 and 4
      newScale = Math.max(1, Math.min(4, newScale));
      
      // 2-finger pan
      const newCenter = getPinchCenter(e.touches);
      const dx = newCenter.x - initialPinchRef.current.center.x;
      const dy = newCenter.y - initialPinchRef.current.center.y;
      
      // HARD STOP at scale 1 - no smaller allowed
      if (newScale <= 1) {
        newScale = 1;
        setScale(1);
        setPosition({ x: 0, y: 0 });
        // Reset ref to prevent further shrinking
        initialPinchRef.current = {
          distance: newDist,
          scale: 1,
          center: newCenter,
          position: { x: 0, y: 0 }
        };
        return; // Stop here, don't allow smaller
      } else {
        // Apply boundaries for 2-finger pan
        const maxPanX = screenWidth * (newScale - 1) * 0.5;
        const maxPanY = screenWidth * (newScale - 1) * 0.7;
        
        setScale(newScale);
        setPosition({
          x: Math.max(-maxPanX, Math.min(maxPanX, initialPinchRef.current.position.x + dx)),
          y: Math.max(-maxPanY, Math.min(maxPanY, initialPinchRef.current.position.y + dy))
        });
      }
    } else if (e.touches.length === 1) {
      const touch = e.touches[0];
      
      if (scale > 1) {
        // Pan when zoomed - WITH BOUNDARIES
        const dx = touch.clientX - lastTouchRef.current.x;
        const dy = touch.clientY - lastTouchRef.current.y;
        
        // Calculate max pan based on zoom level
        // At scale 2, allow pan of screenWidth/2, at scale 4, allow screenWidth*1.5
        const maxPanX = screenWidth * (scale - 1) * 0.5;
        const maxPanY = screenWidth * (scale - 1) * 0.7; // More vertical freedom
        
        setPosition(prev => ({
          x: Math.max(-maxPanX, Math.min(maxPanX, prev.x + dx)),
          y: Math.max(-maxPanY, Math.min(maxPanY, prev.y + dy))
        }));
        lastTouchRef.current = { x: touch.clientX, y: touch.clientY };
      } else if (swipeStartRef.current !== null) {
        // Swipe - move track directly
        const delta = touch.clientX - swipeStartRef.current;
        
        // BLOCK swipe at first/last image
        if ((currentIndex === 0 && delta > 0) || (currentIndex === images.length - 1 && delta < 0)) {
          // Don't allow any movement
          swipeOffsetRef.current = 0;
          return;
        }
        
        swipeOffsetRef.current = delta;
        
        if (trackRef.current) {
          const baseOffset = -currentIndex * screenWidth;
          trackRef.current.style.transition = 'none';
          trackRef.current.style.transform = `translate3d(${baseOffset + delta}px, 0, 0)`;
        }
      }
    }
  };

  const handleTouchEnd = () => {
    initialPinchRef.current = null;
    
    // Always reset to scale 1 if close
    if (scale <= 1.05 && scale !== 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
    
    if (scale <= 1 && swipeStartRef.current !== null) {
      // Determine swipe direction
      const delta = swipeOffsetRef.current;
      const threshold = screenWidth * 0.15; // 15% of screen
      
      let newIndex = currentIndex;
      if (delta < -threshold && currentIndex < images.length - 1) {
        newIndex = currentIndex + 1;
      } else if (delta > threshold && currentIndex > 0) {
        newIndex = currentIndex - 1;
      }
      
      // Snap to position
      goToIndex(newIndex);
    }
    
    swipeStartRef.current = null;
    swipeOffsetRef.current = 0;
  };

  // Double tap
  const lastTapRef = useRef(0);
  const handleTap = (e) => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      // Double tap
      if (scale > 1) {
        setScale(1);
        setPosition({ x: 0, y: 0 });
      } else {
        setScale(2.5);
        setPosition({ x: 0, y: 0 });
      }
    }
    lastTapRef.current = now;
  };

  // Delete with minimum spinner time for UX
  const handleDelete = async () => {
    if (!currentImage || isDeleting || !onDelete) {
      console.log('Delete blocked:', { hasImage: !!currentImage, isDeleting, hasOnDelete: !!onDelete });
      return;
    }
    
    console.log('🗑️ Deleting:', currentImage.generationId, currentImage.imageIndex);
    setIsDeleting(true);
    
    // Minimum spinner time for UX feedback
    const minSpinnerTime = new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      await Promise.all([
        onDelete(currentImage.generationId, currentImage.imageIndex),
        minSpinnerTime
      ]);
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Share
  const handleShare = async () => {
    if (!currentImage) return;
    try {
      const { shareImage } = await import('../../utils/shareMedia');
      await shareImage({ imageUrl: currentImage.url, text: shareTitle, title: shareTitle });
    } catch (err) {
      if (err && err.name !== 'AbortError') console.error(err);
    }
  };

  // Set as Profile Image — öffnet Crop-Modal
  const handleSetProfileImage = () => {
    if (!currentImage || isSavingProfile || !onSetProfileImage) return;
    setShowProfileCrop(true);
  };

  const handleProfileCropSave = async (blob) => {
    setShowProfileCrop(false);
    setIsSavingProfile(true);
    try {
      await onSetProfileImage(currentImage.url, blob);
      setProfileToast(true);
      setTimeout(() => setProfileToast(false), 2000);
    } catch (err) {
      console.error('Profile image error:', err);
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Keyboard
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') goToIndex(currentIndex + 1);
      if (e.key === 'ArrowLeft') goToIndex(currentIndex - 1);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, currentIndex, goToIndex, onClose]);

  // Proxy-URL für externe Bilder (CORS-safe für Canvas-Operationen im Crop-Modal)
  // Kein Proxy — alle Medien liegen lokal
  const getProxyUrl = (url) => url || '';

  if (!isOpen || !images.length) return null;

  return (
    <>
    {createPortal(
    <div
      className="fixed inset-0 bg-black flex flex-col"
      style={{
        zIndex: 999999,
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Top Bar */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 z-10">
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm active:scale-95 transition-transform">
          <X className="w-5 h-5 text-white/70" />
        </button>
        {/* Format Badge - minimalistisch in der Mitte */}
        {displayFormat && (
          <div className="text-white/50 text-sm font-medium">
            {displayFormat}
          </div>
        )}
        <div className="text-white/50 text-sm font-medium">{currentIndex + 1} / {images.length}</div>
      </div>

      {/* Image Track */}
      <div 
        ref={containerRef}
        className="flex-1 relative overflow-hidden"
        style={{ touchAction: 'none' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleTap}
      >
        <div 
          ref={trackRef}
          className="absolute inset-y-0 flex"
          style={{ 
            width: `${images.length * screenWidth}px`,
            willChange: 'transform'
          }}
        >
          {images.map((img, idx) => (
            <div 
              key={img.id || idx}
              className="flex items-center justify-center"
              style={{ width: screenWidth, height: '100%' }}
            >
              <img
                src={img.url}
                alt=""
                className="select-none rounded-2xl"
                style={{
                  maxWidth: '95%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  transform: idx === currentIndex ? `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale})` : 'none',
                  transition: idx === currentIndex ? 'none' : 'transform 0.2s',
                  willChange: 'transform'
                }}
                draggable={false}
                onLoad={idx === currentIndex ? handleImageLoad : undefined}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Desktop Navigation Arrows */}
      {images.length > 1 && (
        <>
          {/* Left Arrow */}
          {currentIndex > 0 && (
            <button
              onClick={() => goToIndex(currentIndex - 1)}
              className="hidden md:flex fixed left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm items-center justify-center z-30 hover:bg-black/60 transition-colors"
            >
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {/* Right Arrow */}
          {currentIndex < images.length - 1 && (
            <button
              onClick={() => goToIndex(currentIndex + 1)}
              className="hidden md:flex fixed right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/40 backdrop-blur-sm items-center justify-center z-30 hover:bg-black/60 transition-colors"
            >
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </>
      )}

      {/* Profile Toast */}
      {profileToast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-white/20 backdrop-blur-md px-6 py-3 rounded-full">
          <span className="text-white text-sm font-medium">✓ Als Profilbild gespeichert</span>
        </div>
      )}

      {/* Delete Toast */}
      {isDeleting && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-white/20 backdrop-blur-md px-6 py-3 rounded-full">
          <span className="text-white text-sm font-medium">Gelöscht</span>
        </div>
      )}

      {/* Quick Delete Button - minimalistisch rechts */}
      {onDelete && (
        <button
          type="button"
          disabled={isDeleting}
          onClick={handleDelete}
          className="fixed bottom-32 right-4 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center z-20 disabled:opacity-50 active:scale-95 transition-transform"
        >
          <Trash2 className="w-5 h-5 text-white/70" />
        </button>
      )}

      {/* Bottom Controls */}
      <div className="flex-shrink-0 flex flex-col items-center pb-4 z-10">
        <div className="flex items-center gap-4 mb-4">
          <button onClick={() => onToggleFavorite?.(currentImage?.id)} className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm active:scale-95 transition-transform">
            <Star className={`w-5 h-5 ${isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-white/40'}`} />
          </button>
          {onSetProfileImage && (
            <button onClick={handleSetProfileImage} disabled={isSavingProfile} className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm active:scale-95 transition-transform disabled:opacity-50">
              {isSavingProfile ? (
                <div className="w-4 h-4 border-2 border-white/40 border-t-transparent rounded-full animate-spin" />
              ) : (
                <User className="w-5 h-5 text-white/40" />
              )}
            </button>
          )}
          <button onClick={handleShare} className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm active:scale-95 transition-transform">
            <Share2 className="w-5 h-5 text-white/40" />
          </button>
          {onVideo && (
            <button onClick={() => onVideo(currentImage)} className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm active:scale-95 transition-transform">
              <Video className="w-5 h-5 text-white/40" />
            </button>
          )}
          {onDelete && (
            <button onClick={handleDelete} disabled={isDeleting} className="w-12 h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm active:scale-95 transition-transform disabled:opacity-50">
              {isDeleting ? (
                <div className="w-4 h-4 border-2 border-white/40 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Trash2 className="w-5 h-5 text-white/40" />
              )}
            </button>
          )}
        </div>
        {images.length > 1 && images.length <= 20 && (
          <div className="flex gap-1.5">
            {images.map((_, idx) => (
              <div key={idx} className={`h-1.5 rounded-full transition-all ${idx === currentIndex ? 'bg-white/80 w-4' : 'bg-white/30 w-1.5'}`} />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  )}
  {showProfileCrop && currentImage && (
    <ProfileCropModal
      imageSrc={getProxyUrl(currentImage.url)}
      onSave={handleProfileCropSave}
      onCancel={() => setShowProfileCrop(false)}
    />
  )}
  </>
  );
};

export default ImageFullscreenViewer;
